import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const LOGO = "";
import { BullMark } from "@/components/dochub/BullMark";
const HERO = "https://images.pexels.com/photos/35518412/pexels-photo-35518412.jpeg";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "branch_user" ? "/scan" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-canvas">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-espresso/70" />
        <div className="relative z-10 p-16 flex flex-col justify-between text-canvas">
          <BullMark className="w-14 h-14" />
          <div>
            <h1 className="font-display text-5xl font-bold leading-tight mb-4">Every receipt.<br/>Every branch.<br/><span className="text-gold">Straight to draft.</span></h1>
            <p className="text-canvas/80 max-w-md font-body">DocHub captures, extracts and files your receipts into Xero &amp; Zoho Books — as drafts, never approved. Human control, artisan care.</p>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-[520px] flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 lg:hidden mb-8">
            <BullMark className="w-10 h-10" />
            <span className="font-display font-bold text-2xl text-espresso">DocHub</span>
          </div>
          <h2 className="font-display text-3xl font-bold text-espresso mb-1">Sign in</h2>
          <p className="text-espresso-ink/70 text-sm mb-8">Enter your credentials to continue.</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1.5 px-4 py-3 bg-white border border-canvas-border rounded-lg text-espresso brand-focus"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Password</label>
                <Link to="/forgot-password" className="text-xs text-gold hover:text-gold-light" data-testid="login-forgot-link">Forgot?</Link>
              </div>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-1.5 px-4 py-3 bg-white border border-canvas-border rounded-lg text-espresso brand-focus"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="w-full py-3 bg-espresso text-canvas rounded-lg font-semibold hover:bg-espresso-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
